import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const FlightAllDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('flight');

  useEffect(() => {
    // Simulate API call to fetch booking details
    const fetchBookingDetails = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Mock booking data
        setBookingDetails({});
      } catch (error) {
        console.error('Error fetching booking details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [searchParams]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    };
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-jet-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-jet-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-jet-gray-900 mb-2">Booking Not Found</h2>
            <p className="text-jet-gray-600 mb-4">The booking details could not be loaded.</p>
            <button
              onClick={() => navigate('/jetsetter/profile/booking-history')}
              className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium py-2 px-4 rounded-md transition-colors"
            >
              Back to Booking History
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
      <main className="py-8">
        <div className="w-[86%] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-jet-gray-600 hover:text-jet-gray-900 transition-colors mb-2"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Back
              </button>
              <h1 className="text-2xl font-bold text-jet-gray-900">Booking Details</h1>
              <p className="text-jet-gray-600">Reference: {bookingDetails.bookingReference}</p>
            </div>
            
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(bookingDetails.status)}`}>
                {bookingDetails.status.charAt(0).toUpperCase() + bookingDetails.status.slice(1)}
              </span>
              <p className="text-sm text-jet-gray-600 mt-1">
                Booked on {formatDateTime(bookingDetails.bookingDate).date}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-jet-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'flight', label: 'Flight Details', icon: '✈️' },
                  { id: 'passengers', label: 'Passengers', icon: '👥' },
                  { id: 'pricing', label: 'Pricing', icon: '💰' },
                  { id: 'services', label: 'Services', icon: '🎯' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-jet-yellow text-jet-yellow'
                        : 'border-transparent text-jet-gray-500 hover:text-jet-gray-700 hover:border-jet-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Flight Details Tab */}
              {activeTab === 'flight' && (
                <div className="space-y-6">
                  {bookingDetails.flights.map((flight, index) => (
                    <div key={flight.id} className="border border-jet-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-jet-gray-900">{flight.flightNumber}</h3>
                          <p className="text-sm text-jet-gray-600">{flight.airline} • {flight.aircraft}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          flight.status === 'On Time' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {flight.status}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-3 gap-6">
                        {/* Departure */}
                        <div>
                          <h4 className="font-medium text-jet-gray-900 mb-2">Departure</h4>
                          <div className="space-y-1 text-sm">
                            <p className="font-medium text-lg">{flight.departure.airport}</p>
                            <p className="text-jet-gray-600">{flight.departure.airportName}</p>
                            <p className="text-jet-gray-600">{flight.departure.city}, {flight.departure.country}</p>
                            <div className="mt-2 pt-2 border-t border-jet-gray-200">
                              <p className="font-medium">{formatDateTime(flight.departure.dateTime).date}</p>
                              <p className="text-lg font-bold text-jet-blue-600">{formatDateTime(flight.departure.dateTime).time}</p>
                              <p className="text-jet-gray-600">Terminal {flight.departure.terminal} • Gate {flight.departure.gate}</p>
                            </div>
                          </div>
                        </div>

                        {/* Flight Info */}
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-2">
                            <div className="flex-1 border-t border-jet-gray-300"></div>
                            <div className="px-4">
                              <svg className="w-6 h-6 text-jet-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                              </svg>
                            </div>
                            <div className="flex-1 border-t border-jet-gray-300"></div>
                          </div>
                          <p className="text-sm text-jet-gray-600 mb-1">Duration</p>
                          <p className="font-medium">{flight.duration}</p>
                          <p className="text-sm text-jet-gray-600 mt-2">{flight.class}</p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="font-medium text-jet-gray-900 mb-2">Arrival</h4>
                          <div className="space-y-1 text-sm">
                            <p className="font-medium text-lg">{flight.arrival.airport}</p>
                            <p className="text-jet-gray-600">{flight.arrival.airportName}</p>
                            <p className="text-jet-gray-600">{flight.arrival.city}, {flight.arrival.country}</p>
                            <div className="mt-2 pt-2 border-t border-jet-gray-200">
                              <p className="font-medium">{formatDateTime(flight.arrival.dateTime).date}</p>
                              <p className="text-lg font-bold text-jet-blue-600">{formatDateTime(flight.arrival.dateTime).time}</p>
                              <p className="text-jet-gray-600">Terminal {flight.arrival.terminal} • Gate {flight.arrival.gate}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Passengers Tab */}
              {activeTab === 'passengers' && (
                <div className="space-y-4">
                  {bookingDetails.passengers.map((passenger, index) => (
                    <div key={passenger.id} className="border border-jet-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-jet-gray-900">
                            {passenger.title} {passenger.firstName} {passenger.lastName}
                          </h3>
                          <p className="text-sm text-jet-gray-600">Passenger {index + 1}</p>
                        </div>
                        <span className="bg-jet-blue-100 text-jet-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          Seat {passenger.seatNumber}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-jet-gray-700">Email</label>
                            <p className="text-jet-gray-900">{passenger.email}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-jet-gray-700">Phone</label>
                            <p className="text-jet-gray-900">{passenger.phone}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-jet-gray-700">Date of Birth</label>
                            <p className="text-jet-gray-900">{new Date(passenger.dateOfBirth).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-jet-gray-700">Nationality</label>
                            <p className="text-jet-gray-900">{passenger.nationality}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-jet-gray-700">Passport Number</label>
                            <p className="text-jet-gray-900 font-mono">{passenger.passportNumber}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="space-y-6">
                  <div className="border border-jet-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Price Breakdown</h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-jet-gray-600">Base Fare ({bookingDetails.passengers.length} passenger{bookingDetails.passengers.length > 1 ? 's' : ''})</span>
                        <span className="text-jet-gray-900">${bookingDetails.pricing.baseFare.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-jet-gray-600">Taxes & Fees</span>
                        <span className="text-jet-gray-900">${(bookingDetails.pricing.taxes + bookingDetails.pricing.fees).toFixed(2)}</span>
                      </div>
                      {bookingDetails.pricing.discount < 0 && (
                        <div className="flex justify-between">
                          <span className="text-green-600">Discount</span>
                          <span className="text-green-600">${bookingDetails.pricing.discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-jet-gray-200 pt-3">
                        <div className="flex justify-between font-semibold text-lg">
                          <span className="text-jet-gray-900">Total Amount</span>
                          <span className="text-jet-gray-900">${bookingDetails.pricing.total.toFixed(2)} {bookingDetails.currency}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-jet-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Payment Information</h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-jet-gray-700">Payment Method</label>
                          <p className="text-jet-gray-900">{bookingDetails.payment.method}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-jet-gray-700">Card</label>
                          <p className="text-jet-gray-900">{bookingDetails.payment.cardType} ending in {bookingDetails.payment.lastFour}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-jet-gray-700">Transaction ID</label>
                          <p className="text-jet-gray-900 font-mono">{bookingDetails.payment.transactionId}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-jet-gray-700">Payment Date</label>
                          <p className="text-jet-gray-900">{formatDateTime(bookingDetails.payment.paymentDate).date}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Services Tab */}
              {activeTab === 'services' && (
                <div className="space-y-4">
                  <div className="border border-jet-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Additional Services</h3>
                    
                    <div className="space-y-3">
                      {bookingDetails.services.map((service, index) => (
                        <div key={index} className="flex items-center justify-between py-3 border-b border-jet-gray-100 last:border-b-0">
                          <div>
                            <p className="font-medium text-jet-gray-900">{service.name}</p>
                            <p className="text-sm text-jet-gray-600">{service.status}</p>
                          </div>
                          <span className="font-medium text-jet-gray-900">
                            {service.price > 0 ? `$${service.price.toFixed(2)}` : 'Included'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate(`/invoice/${bookingDetails.bookingReference}`)}
              className="flex items-center px-6 py-3 bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium rounded-md transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              View Invoice
            </button>
            
            <button
              onClick={() => navigate('/check-in')}
              className="flex items-center px-6 py-3 border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium rounded-md transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Check In
            </button>
            
            <button
              onClick={() => toast.info('Contact support functionality would be implemented here')}
              className="flex items-center px-6 py-3 border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium rounded-md transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
              Contact Support
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FlightAllDetailsPage;